<script lang="ts">
	//Design system components
	import { DeviceButton, DeviceButtonV2, Label, LabelV2 } from '@Shelly/shelly-design-system';
	//Helpers
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { mappedInputReaderEvent } from 'src/helpers/input_reader_helpers';
	import { format } from 'svelte-i18n';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import type {
		DeviceButtonPropsTypes,
		DeviceButtonV2PropsTypes,
		LabelPropsTypes,
	} from '@Shelly/shelly-design-system';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device: Device,
		componentID: number,
		value: string,
		icon: string = null,
		isLoading = false,
		isWide = false,
		hasDarkIcon = false,
		disabled = false,
		size: DeviceButtonPropsTypes['size'] = 'm';
	export let useV2Components = false;
	export let labelProps: Partial<LabelPropsTypes> = {};
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 'm',
	};

	const longPushTime = 800;
	const singlePushTime = 250;
	const clickEvents = ['single_push', 'double_push', 'triple_push'];

	$: hasRights = hasControlRights(device);

	let init = false;
	let btnState: string = '';
	let statusTimeoutId: NodeJS.Timeout;
	let clickTimeoutId: NodeJS.Timeout;
	let mouseDownTS = 0;
	let clicksCount = 0;

	$: changeState(value);

	const changeState = (value: string) => {
		clearTimeout(statusTimeoutId);
		if (init && /[SL]/.test(value)) {
			btnState = value;
		}
		init = true;
		statusTimeoutId = setTimeout(() => (btnState = ''), 1500);
	};

	const initButton = (btn: HTMLElement) => {
		if (!disabled) {
			btn.addEventListener('ontouchstart' in window ? 'touchstart' : 'mousedown', onBtnDown);
			btn.addEventListener('ontouchend' in window ? 'touchend' : 'mouseup', onBtnUp);
		}
		return {
			destroy() {
				clearTimeout(statusTimeoutId);
				clearTimeout(clickTimeoutId);
				btn.removeEventListener('touchstart', onBtnDown);
				btn.removeEventListener('mousedown', onBtnDown);
				btn.removeEventListener('touchend', onBtnUp);
				btn.removeEventListener('mouseup', onBtnUp);
			},
		};
	};

	const onBtnDown = (e: MouseEvent) => {
		clearTimeout(statusTimeoutId);
		clearTimeout(clickTimeoutId);
		mouseDownTS = e.timeStamp;
	};

	const onBtnUp = (e: MouseEvent) => {
		if (e.timeStamp - mouseDownTS > longPushTime) {
			if (clicksCount == 0) trigger('long_push');
		} else {
			const _clicksCount: number = clicksCount++;
			clickTimeoutId = setTimeout(() => trigger(clickEvents[_clicksCount]), singlePushTime);
		}
	};

	const trigger = async (event: string) => {
		clicksCount = 0;
		if (!event) return;
		isLoading = true;
		const res: SettingRequest = await gen2Request(device, 'Button.Trigger', {
			id: componentID,
			event,
		});
		if (!res?.isok) onSettingsResponse(res, device);
		isLoading = false;
	};
</script>

{#if disabled}
	{#if useV2Components}
		<LabelV2
			value="N/A"
			icon="fas fa-hand-back-point-up"
			dotsType={mappedInputReaderEvent[btnState]}
			dotsColor={btnState ? 'primary' : 'default'}
			size={deviceButtonV2Props.size == 'm' ? 'l' : 'm'}
			{...labelProps}
		/>
	{:else}
		<Label
			value="N/A"
			icon="fas fa-hand-back-point-up"
			dotsType={mappedInputReaderEvent[btnState]}
			dotsColor={btnState ? 'primary' : 'default'}
			size="l"
		/>
	{/if}
{:else}
	<div
		class="virtual-button"
		role="presentation"
		use:initButton
		on:click={(e) => {
			e.stopPropagation();
		}}
	>
		<div class={`dots-container${hasDarkIcon ? ' has-dark-icon' : ''}`}>
			{#if (!icon && /^S$/.test(btnState)) || /SS/.test(btnState)}
				<span class="dot" />
			{/if}
			{#if /SS/.test(btnState)}
				<span class="dot" />
			{/if}
			{#if /SSS/.test(btnState)}
				<span class="dot" />
			{/if}
			{#if /^L$/.test(btnState)}
				<span class="dash" />
			{/if}
		</div>

		{#if useV2Components}
			<DeviceButtonV2
				id="devicebutton-y14fhgl"
				aria-label={$format('label_button', { default: 'Button' }) +
					(btnState ? `: ${mappedInputReaderEvent[btnState]}` : '')}
				class={`${disabled ? 'disabled' : ''}`}
				icon={(!icon && /^S$/.test(btnState)) || /(^L$)|(SS)/.test(btnState)
					? null
					: icon || 'far fa-circle'}
				{isLoading}
				isActive={icon && /^S$/.test(btnState)}
				hasDarkIcon={hasDarkIcon && btnState == ''}
				disabled={!hasRights}
				{...deviceButtonV2Props}
			/>
		{:else}
			<DeviceButton
				id="devicebutton-y14fhgl"
				aria-label={$format('label_button', { default: 'Button' }) +
					(btnState ? `: ${mappedInputReaderEvent[btnState]}` : '')}
				class={`${disabled ? 'disabled' : ''}`}
				icon={(!icon && /^S$/.test(btnState)) || /(^L$)|(SS)/.test(btnState)
					? null
					: icon || 'far fa-circle'}
				{isLoading}
				isActive={icon && /^S$/.test(btnState)}
				size={isWide ? 'l' : size}
				hasDarkIcon={hasDarkIcon && btnState == ''}
				{isWide}
				disabled={!hasRights}
			/>
		{/if}
	</div>
{/if}

<style>
	.virtual-button {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}
	.dots-container {
		display: flex;
		position: absolute;
		justify-content: center;
		align-items: center;
		width: 56px;
		height: 56px;
		z-index: 11;
		pointer-events: none;
	}
	.dots-container span {
		border-radius: 50%;
		background-color: var(--color-primary);
	}
	.dots-container.has-dark-icon span {
		background-color: var(--color-1);
	}
	.dots-container .dot {
		width: 5px;
		height: 5px;
		margin: 1px;
	}
	.dots-container .dash {
		width: 17px;
		height: 3px;
		border-radius: var(--radius-small);
	}
</style>
